import type { Collaboration, CollaborationFilters, CreateCollaborationData, UpdateCollaborationData } from "@/types/collaboration";

// Mock data per collaborations
const mockCollaborations: Collaboration[] = [
    {
        id: "1",
        shipperId: "1", // TechCorp Solutions LLC
        receiverId: "2", // Global Partners Corp
        status: "Active",
        invitedAt: new Date("2024-01-10"),
        acceptedAt: new Date("2024-01-12"),
        notes: "Partnership strategica per esportazioni tech",
    },
    {
        id: "2",
        shipperId: "1", // TechCorp Solutions LLC
        receiverId: "4", // Green Future NGO
        status: "Active",
        invitedAt: new Date("2024-01-15"),
        acceptedAt: new Date("2024-01-18"),
        notes: "Collaborazione per progetti sostenibili",
    },
    {
        id: "3",
        shipperId: "1", // TechCorp Solutions LLC
        receiverId: "8", // Health Foundation NGO
        status: "Active",
        invitedAt: new Date("2024-01-05"),
        acceptedAt: new Date("2024-01-08"),
        notes: "Supporto per tecnologie mediche",
    },
    {
        id: "4",
        shipperId: "3", // Innovation Hub Partnership
        receiverId: "6", // European Trading Co.
        status: "Active",
        invitedAt: new Date("2024-01-08"),
        acceptedAt: new Date("2024-01-10"),
        notes: "Partnership europea per innovazione",
    },
    {
        id: "5",
        shipperId: "3", // Innovation Hub Partnership
        receiverId: "4", // Green Future NGO
        status: "Pending",
        invitedAt: new Date("2024-01-25"),
        notes: "In attesa di risposta per progetto green tech",
    },
    {
        id: "6",
        shipperId: "7", // Sole Proprietor Consulting
        receiverId: "8", // Health Foundation NGO
        status: "Active",
        invitedAt: new Date("2024-01-20"),
        acceptedAt: new Date("2024-01-22"),
        notes: "Consulenza per progetti sanitari",
    },
    {
        id: "7",
        shipperId: "7", // Sole Proprietor Consulting
        receiverId: "6", // European Trading Co.
        status: "Rejected",
        invitedAt: new Date("2024-01-18"),
        notes: "Richiesta rifiutata - non interessati",
    },
    {
        id: "8",
        shipperId: "1", // TechCorp Solutions LLC
        receiverId: "6", // European Trading Co.
        status: "Suspended",
        invitedAt: new Date("2024-01-12"),
        acceptedAt: new Date("2024-01-15"),
        notes: "Collaborazione sospesa per problemi contrattuali",
    },
];

// In-memory storage
let collaborations = [...mockCollaborations];

// Helper functions per CRUD operations
export const collaborationService = {
    // Get all collaborations
    getAll: (): Collaboration[] => {
        return [...collaborations];
    },

    // Get collaboration by ID
    getById: (id: string): Collaboration | undefined => {
        return collaborations.find((collaboration) => collaboration.id === id);
    },

    // Get collaborations by shipper ID
    getByShipperId: (shipperId: string): Collaboration[] => {
        return collaborations.filter((collaboration) => collaboration.shipperId === shipperId);
    },

    // Get collaborations by receiver ID
    getByReceiverId: (receiverId: string): Collaboration[] => {
        return collaborations.filter((collaboration) => collaboration.receiverId === receiverId);
    },

    // Create new collaboration (invite)
    create: (data: CreateCollaborationData): Collaboration => {
        const newCollaboration: Collaboration = {
            id: (collaborations.length + 1).toString(),
            ...data,
            status: "Pending",
            invitedAt: new Date(),
        };
        collaborations.push(newCollaboration);
        return newCollaboration;
    },

    // Update collaboration
    update: (id: string, data: UpdateCollaborationData): Collaboration | null => {
        const index = collaborations.findIndex((collaboration) => collaboration.id === id);
        if (index === -1) return null;

        const updateData: Partial<Collaboration> = { ...data };
        if (data.status === "Active" && !collaborations[index].acceptedAt) {
            updateData.acceptedAt = new Date();
        }

        collaborations[index] = {
            ...collaborations[index],
            ...updateData,
        };
        return collaborations[index];
    },

    // Delete collaboration
    delete: (id: string): boolean => {
        const index = collaborations.findIndex((collaboration) => collaboration.id === id);
        if (index === -1) return false;

        collaborations.splice(index, 1);
        return true;
    },

    // Search and filter collaborations
    search: (filters: CollaborationFilters): Collaboration[] => {
        let filtered = [...collaborations];

        if (filters.search) {
            const searchTerm = filters.search.toLowerCase();
            filtered = filtered.filter((collaboration) => collaboration.notes?.toLowerCase().includes(searchTerm));
        }

        if (filters.status) {
            filtered = filtered.filter((collaboration) => collaboration.status === filters.status);
        }

        if (filters.shipperId) {
            filtered = filtered.filter((collaboration) => collaboration.shipperId === filters.shipperId);
        }

        if (filters.receiverId) {
            filtered = filtered.filter((collaboration) => collaboration.receiverId === filters.receiverId);
        }

        return filtered;
    },

    // Get collaboration statistics
    getStats: (shipperId?: string): { total: number; active: number; pending: number; rejected: number; suspended: number } => {
        let filteredCollaborations = shipperId ? collaborations.filter((c) => c.shipperId === shipperId) : collaborations;

        return {
            total: filteredCollaborations.length,
            active: filteredCollaborations.filter((collaboration) => collaboration.status === "Active").length,
            pending: filteredCollaborations.filter((collaboration) => collaboration.status === "Pending").length,
            rejected: filteredCollaborations.filter((collaboration) => collaboration.status === "Rejected").length,
            suspended: filteredCollaborations.filter((collaboration) => collaboration.status === "Suspended").length,
        };
    },
};
